/* stylelint-disable a11y/media-prefers-reduced-motion */
/* stylelint-disable color-no-hex  */
/* stylelint-disable no-descending-specificity */

import { Controlled as Codemirror } from 'react-codemirror2';
import styled, { injectGlobal } from 'styled-components';

import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';

import 'codemirror/lib/codemirror.css';
import 'codemirror/addon/hint/show-hint.css';
import {
  DEFAULT_DANGER_COLOR,
  DEFAULT_LINK_COLOR,
  DEFAULT_SUCCESS_COLOR,
  DEFAULT_TEXT_COLOR,
} from 'src/components/pipelines/constants';

// Code mirror addons
require('codemirror/mode/javascript/javascript');
require('codemirror/mode/yaml/yaml');
require('codemirror/addon/display/autorefresh');
require('codemirror/addon/lint/lint');
require('codemirror/addon/hint/show-hint');

const gridSize = token('space.100', '8px');

// eslint-disable-next-line @typescript-eslint/no-unused-expressions
injectGlobal`
  /* Lint tooltip wrapper */
  .CodeMirror-lint-tooltip {
    box-shadow: ${token(
      'elevation.shadow.overlay',
      `0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`
    )};
    background-color: ${token(
      'color.background.accent.gray.subtlest',
      colors.N20
    )};
    color: ${token('color.text.subtle', colors.N500)};
    border-radius: 4px;
    font: 400 11px/1.5 -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
      Noto Sans, Oxygen, Ubuntu, Droid Sans, Helvetica Neue, sans-serif;
    overflow: hidden;
    padding: ${gridSize};
    position: fixed;
    white-space: pre-wrap;
    z-index: 1000;
    max-width: 600px;
    transition: opacity 0.4s;
    .CodeMirror-lint-message-warning,
    .CodeMirror-lint-message-error {
      &::after {
        left: 10px;
        width: 10px;
        height: 10px;
        margin-top: -5px;
      }
    }
  }

  /* Lint message */
  .CodeMirror-lint-message-error,
  .CodeMirror-lint-message-warning {
    padding-left: 18px;
    background-position: top left;
    background-repeat: no-repeat;
  }

  /* Lint marker and message combination */
  .CodeMirror-lint-marker-error,
  .CodeMirror-lint-message-error,
  .CodeMirror-lint-marker-warning,
  .CodeMirror-lint-message-warning {
    &::after {
      display: block;
      content: '';
      border-radius: 10px;
      width: 6px;
      height: 6px;
      position: absolute;
      top: 50%;
      margin-top: -4px;
    }
  }

  .CodeMirror .bidi-replacement {
    background: ${token('color.background.warning.bold', colors.Y500)};
    color: ${token('color.text.inverse', colors.N0)};
    cursor: pointer;
    margin: 0 4px;
    padding: 0 2px;
  }

  .CodeMirror-lint-message-error::after,
  .CodeMirror-lint-marker-error::after {
    background: ${token('color.background.danger.bold', colors.R500)};
  }

  .CodeMirror-lint-message-warning::after,
  .CodeMirror-lint-marker-warning::after {
    background: ${token('color.background.warning.bold', colors.Y500)};
  }

  .cm-indent-guides {
    position: relative;
    display: inline-block;
    border-left: 1px solid ${token('color.border', colors.N300A)};
    width: 7px;
    height: 100%;
  }
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression
export const StyledCodemirror = styled(Codemirror)`
  width: 100%;
  height: 100%;
  .CodeMirror.cm-s-default {
    height: 100%;
    min-height: 500px;
    background-color: ${token('elevation.surface', colors.N0)};
    font: ${token('font.body.small')};
    font-family: ${token(
      'font.family.code',
      'Monaco, Consolas, "Andale Mono WT", "Andale Mono", "Lucida Console", "Lucida Sans Typewriter", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Liberation Mono", "Nimbus Mono L", "Courier New", Courier, monospace'
    )};
    font-weight: ${token('font.weight.medium')};

    .cm-comment {
      color: ${token('color.text.subtlest', colors.B500)};
    }

    .cm-string,
    .cm-string-2 {
      color: ${token('color.text.accent.orange', colors.B500)};
    }

    .cm-attribute {
      color: ${token('color.text.success', DEFAULT_SUCCESS_COLOR)};
    }

    .cm-variable {
      color: ${token('color.text.accent.gray', colors.DN800)};
    }

    .cm-variable-2 {
      color: ${token('color.text.accent.blue.bolder', colors.B200)};
    }

    .cm-variable-3 {
      color: ${token('color.text.accent.blue', colors.B300)};
    }

    .cm-number {
      color: ${token('color.text.accent.blue', colors.B500)};
    }

    .cm-atom {
      color: ${token('color.text.accent.orange', colors.P400)};
    }

    .cm-qualifier {
      color: ${token('color.text.accent.blue.bolder', colors.B200)};
    }

    .cm-tag {
      color: ${token('color.text.brand', colors.B400)};
    }

    .cm-meta {
      color: ${token('color.text.subtle', colors.N800)};
    }

    .cm-operator {
      color: ${token('color.text', DEFAULT_TEXT_COLOR)};
    }

    .cm-def,
    .cm-property {
      color: ${token('color.text.success', DEFAULT_SUCCESS_COLOR)};
    }

    .cm-keyword {
      color: ${token('color.text.accent.red', colors.R300)};
    }

    .cm-bracket {
      color: ${token('color.text', DEFAULT_TEXT_COLOR)};
    }

    .cm-type {
      color: ${token('color.text.accent.blue.bolder', colors.B200)};
    }

    .cm-builtin {
      color: ${token('color.text.accent.blue.bolder', colors.B200)};
    }

    .cm-hr {
      color: ${token('color.text.accent.blue.bolder', colors.B200)};
    }

    .cm-link {
      color: ${token('color.link', DEFAULT_LINK_COLOR)};
    }

    .cm-punctuation {
      color: ${token('color.text', DEFAULT_TEXT_COLOR)};
    }

    .cm-quote {
      color: ${token('color.text', DEFAULT_TEXT_COLOR)};
    }

    .cm-negative {
      color: ${token('color.text.danger', DEFAULT_DANGER_COLOR)};
    }

    .cm-positive {
      color: ${token('color.text.success', DEFAULT_SUCCESS_COLOR)};
    }

    .cm-invalidchar {
      color: ${token('color.border.danger', colors.R500)};
    }

    pre.CodeMirror-line {
      word-break: break-all;
      color: ${token('color.text', DEFAULT_TEXT_COLOR)};
    }

    /* Editor addons */
    .CodeMirror-linenumber {
      padding-right: ${token('space.100', '8px')};
    }
    .CodeMirror-gutters {
      background-color: ${token('elevation.surface', colors.N0)};
      border-right: 1px ${token('color.border', colors.N50)} solid;
    }
    .CodeMirror-linenumbers {
      width: calc(4px * ${gridSize});
    }

    /* Lint markers (dots in gutter) */
    .CodeMirror-lint-markers {
      width: calc(2px * ${gridSize});
    }

    .CodeMirror-lint-marker-error,
    .CodeMirror-lint-marker-warning {
      background-repeat: no-repeat;
      cursor: pointer;
      display: inline-block;
      margin: 0 0 ${token('space.025', '2px')} 10px;
      height: calc(2px * ${gridSize});
      width: calc(2px * ${gridSize});
      vertical-align: middle;
      position: relative;
    }

    /* Lint mark (squiggly line under offending text) */
    .CodeMirror-lint-mark-error,
    .CodeMirror-lint-mark-warning {
      background-position: left bottom;
      background-repeat: repeat-x;
    }
    .CodeMirror-lint-mark-error {
      background-image: url('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAADCAYAAAC09K7GAAAAAXNSR0IArs4c6QAAAAZiS0dEAP8A/wD/oL2nkwAAAAlwSFlzAAALEwAACxMBAJqcGAAAAAd0SU1FB9sJDw4cOCW1/KIAAAAZdEVYdENvbW1lbnQAQ3JlYXRlZCB3aXRoIEdJTVBXgQ4XAAAAHElEQVQI12NggIL/DAz/GdA5/xkY/qPKMDAwAADLZwf5rvm+LQAAAABJRU5ErkJggg==');
    }
    .CodeMirror-lint-mark-warning.warning-style:not(.cm-comment) {
      color: ${token('color.text.danger', colors.R200)};
    }
  }
  .CodeMirror-selected {
    background-color: ${token(
      'color.background.accent.purple.bolder',
      colors.P500
    )};
    opacity: 0.3;
  }
  .CodeMirror-cursor {
    border-left: 1px solid
      ${token('color.background.accent.blue.subtle', colors.B500)};
  }
`;
